import { Button, Header, Icon, Message, Tooltip } from '@mairistem/composants';
import { ListeTable } from '@mairistem/liste';

import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import { ETATS_FACTURE } from '@/constants';
import { RegieContext } from '@/contexts';
import type { FactureListeItemType } from '@/types/facture';
import { formatMonetaire } from '@/utils';

// @ts-expect-error
import icon from '../../assets/images/icon_encaisser_white.svg';

interface FacturesListeTableRowProps {
  item: FactureListeItemType;
  /** Désactive l'encaissement si la date limite est dépassée */
  staled?: boolean;
  /** Désactive l'encaissement suite à un arrêté périodique */
  encaissementBloque?: boolean;
  rowProps: Record<string, unknown>;
  /** Télécharge le PDF de la facture à partir de l'identifiant de son document */
  onOpenPdf: (identifiantDocument: number) => void;
  onEncaissement: (id: number) => void;
}

const FacturesListeTableRow = (props: FacturesListeTableRowProps) => {
  const {
    item,
    staled = false,
    encaissementBloque = false,
    rowProps,
    onOpenPdf,
    onEncaissement,
  } = props;

  const disabled = staled || encaissementBloque;

  const [doubleClickTries, setDoubleClickTries] = React.useState(0);

  const { regies, selectedRegie } = React.useContext(RegieContext);
  // Si 1 seul logiciel facturier, on masque la colonne
  const hideSourceColumn =
    _.size(_.find(regies, { identifiant: selectedRegie })?.logicielsFacturier) <= 1;

  const {
    identifiant,
    numero,
    nomPayeur,
    nomFacturation,
    cycleFacturation,
    source,
    dateFacture,
    dateFactureProlongee,
    dateEmission,
    adresseFacturation,
    restePayer,
    etat,
    identifiantDocument,
  } = item;

  const handleDoubleClick = React.useCallback(
    (incrementBy) => () => {
      if (disabled) {
        setDoubleClickTries((prev) => prev + incrementBy);
      } else {
        onEncaissement(identifiant);
      }
    },
    [disabled, identifiant, onEncaissement],
  );

  React.useEffect(() => {
    if (doubleClickTries > 1) {
      setDoubleClickTries(1);

      toast(
        <Header as="h4" style={{ margin: 0 }}>
          Encaissement désactivé
          <Header.Subheader style={{ fontStyle: 'italic', marginTop: 8 }}>
            {encaissementBloque
              ? 'Un arrêté périodique a été créé ce jour, les encaissements sont bloqués'
              : "La date limite d'encaissement de cette facture est dépassée"}
          </Header.Subheader>
        </Header>,
        {
          type: 'warning',
          closeOnClick: true,
          pauseOnFocusLoss: false,
        },
      );
    }
  }, [doubleClickTries, encaissementBloque]);

  return (
    <ListeTable.Row key={identifiant} {...rowProps}>
      <ListeTable.Cell
        name="numero"
        // @ts-expect-error
        title={numero}
        style={{ width: 175 }}
        onDoubleClick={handleDoubleClick(1)}
      >
        <div style={{ display: 'flex' }}>
          <div className="textEllipsis" style={{ paddingRight: 10, flex: 1 }}>
            {numero}
          </div>
          {!_.isNil(identifiantDocument) && (
            <Tooltip
              mouseEnterDelay={100}
              // @ts-expect-error
              flowing
              position="bottom center"
              trigger={
                <Icon
                  data-testid="button-pdf"
                  name="file pdf"
                  style={{ float: 'right' }}
                  fitted
                  onClick={() => onOpenPdf(identifiantDocument)}
                  // Le double-clic sur l'icône ne doit pas déclencher l'encaissement
                  onDoubleClick={(e) => e.stopPropagation()}
                  link
                  size="large"
                  title=""
                />
              }
              message={<>Accéder à la facture PDF</>}
            />
          )}
        </div>
      </ListeTable.Cell>
      <ListeTable.Cell
        name="nomPayeur"
        // @ts-expect-error
        title={nomPayeur}
        onDoubleClick={handleDoubleClick(1)}
      >
        <div style={{ display: 'flex' }}>
          <div className="textEllipsis" style={{ paddingRight: 10, flex: 1 }}>
            {nomPayeur}
          </div>
        </div>
      </ListeTable.Cell>
      <ListeTable.Cell
        name="nomFacturation"
        // @ts-expect-error
        title={nomFacturation}
        onDoubleClick={handleDoubleClick(1)}
      >
        {nomFacturation}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="adresseFacturation"
        style={{ width: 200 }}
        // @ts-expect-error
        title={adresseFacturation}
        onDoubleClick={handleDoubleClick(1)}
      >
        {adresseFacturation}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="cycleFacturation"
        // @ts-expect-error
        title={`${cycleFacturation} - ${dayjs(dateEmission).format('DD/MM/YYYY')}`}
        onDoubleClick={handleDoubleClick(1)}
        style={{ position: 'relative' }}
      >
        <div style={{ display: 'flex' }}>
          <div className="textEllipsis" style={{ paddingRight: 10, flex: 1 }}>
            {cycleFacturation}
          </div>
          <span className="hint">{dayjs(dateEmission).format('DD/MM/YYYY')}</span>
        </div>
      </ListeTable.Cell>
      {!hideSourceColumn && (
        <ListeTable.Cell
          name="source"
          textAlign="center"
          style={{ width: 90 }}
          // @ts-expect-error
          onDoubleClick={handleDoubleClick(1)}
        >
          {source}
        </ListeTable.Cell>
      )}
      <ListeTable.Cell
        name="dateFacture"
        textAlign="center"
        style={{ width: 120 }}
        // @ts-expect-error
        onDoubleClick={handleDoubleClick(1)}
        {...(dateFactureProlongee && {
          title: `Date limite non prolongée : ${dayjs(dateFacture).format('DD/MM/YYYY')}`,
        })}
      >
        {(() => {
          const dateLimiteEffective = dateFactureProlongee || dateFacture;
          const joursRestants = dayjs(dateLimiteEffective).diff(dayjs(), 'day');
          const estDans7DerniersJours = joursRestants >= 0 && joursRestants < 7;
          const dateAffichee = dayjs(dateLimiteEffective).format('DD/MM/YYYY');

          return estDans7DerniersJours ? (
            <Header as="h5" color="orange" style={{ fontWeight: 500 }}>
              {dateAffichee}
            </Header>
          ) : (
            dateAffichee
          );
        })()}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="montantAEncaisser"
        textAlign="right"
        style={{ width: 130 }}
        // @ts-expect-error
        onDoubleClick={handleDoubleClick(1)}
      >
        {formatMonetaire(restePayer)}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="etat"
        stretched
        style={{ width: 200 }}
        {...(disabled
          ? { onClick: handleDoubleClick(2) }
          : { onDoubleClick: handleDoubleClick(1) })}
      >
        <Message
          {...(etat !== ETATS_FACTURE.A_ENCAISSER && {
            color:
              etat === ETATS_FACTURE.ENCAISSEMENT_PARTIEL
                ? 'yellow'
                : etat === ETATS_FACTURE.IMPAYE_PARTIEL
                  ? 'orange'
                  : 'red',
          })}
          attached
          style={{
            display: 'flex',
            height: '100%',
            width: '100%',
            margin: 0,
            border: 'none',
            boxShadow: 'none',
            borderRadius: 0,
            padding: 0,
            ...(etat === ETATS_FACTURE.A_ENCAISSER ? { backgroundColor: 'transparent' } : {}),
          }}
        >
          <span
            style={{
              flex: 1,
              overflowX: 'hidden',
              textOverflow: 'ellipsis',
              padding: '1.25em 1.5em',
            }}
          >
            {etat}
          </span>
          <Button
            data-testid="liste-table-row-button-encaissement"
            className="liste-table-row-button"
            title="Encaisser la facture"
            onClick={handleDoubleClick(2)}
            disabled={disabled}
          >
            {disabled && (
              <Icon name="X" color="white" iconSet="Lucide" size={60} strokeWidth={1.75} />
            )}
            <img
              src={icon}
              style={{
                width: 40,
                height: 30,
              }}
              alt="icon_encaisser"
            />
          </Button>
        </Message>
      </ListeTable.Cell>
    </ListeTable.Row>
  );
};

export default FacturesListeTableRow;
